import { useRef, useState } from 'react';
import { Copy, Trash } from 'lucide-react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useDeleteSharedListMutation, useDuplicateSharedListMutation } from 'services/contacts';

export const useSharedListsActions = ({ list, isManager, showDuplicateList = true, queryParams }) => {
  const [deleteList, setDeleteList] = useState({ showModal: false, list: null });
  const [duplicateList, setDuplicateList] = useState({ showModal: false, list: null });
  const menuRef = useRef();
  const history = useHistory();
  const activeNetwork = useSelector(activeNetworkSelector);

  const [deleteSharedList, { isLoading: isLoadingDelete }] = useDeleteSharedListMutation();
  const [duplicateSharedList, { isLoading: isLoadingDuplicate }] = useDuplicateSharedListMutation();

  const allMenuItems = [
    {
      id: 'duplicate',
      isDisabled: false,
      title: 'Duplicate',
      icon: Copy,
      onClick: () => {
        setDuplicateList({ showModal: true, list });
      },
    },
    {
      id: 'delete',
      isDisabled: !isManager,
      title: 'Delete',
      icon: Trash,
      onClick: () => {
        setDeleteList({ showModal: true, list });
      },
    },
  ];

  const actionMenuItems = allMenuItems.filter((item) => !(item.id === 'duplicate' && !showDuplicateList));

  const handleDelete = async (listToDelete) => {
    try {
      await deleteSharedList({
        listId: listToDelete.id,
        queryParams,
      }).unwrap();
      toast('List deleted', { icon: 'success', id: 'list-deleted-successfully' });
      setDeleteList({ showModal: false, list: null });

      // Navigate to lists page if not already there
      if (activeNetwork?.slug && !window.location.pathname.includes('/lists')) {
        history.push(`/networks/${activeNetwork.slug}/lists`);
      }
    } catch {
      toast('Error deleting list, please try again later', { icon: 'danger', id: 'delete-list-error' });
    }
  };

  const handleDuplicate = async (listToDuplicate, newName) => {
    try {
      const result = await duplicateSharedList({
        listId: listToDuplicate.id,
        name: newName,
        queryParams,
      }).unwrap();
      toast('List duplicated successfully', { icon: 'success', id: 'list-duplicated-successfully' });
      setDuplicateList({ showModal: false, list: null });

      // Navigate to the newly duplicated list
      if (result?.sharedList?.id && activeNetwork?.slug) {
        history.push(`/networks/${activeNetwork.slug}/list/${result.sharedList.id}/contacts`);
      }
    } catch {
      toast('Error duplicating list, please try again later', { icon: 'danger', id: 'duplicate-list-error' });
    }
  };

  const closeDeleteModal = () => {
    setDeleteList({ showModal: false, list: null });
  };

  const closeDuplicateModal = () => {
    setDuplicateList({ showModal: false, list: null });
  };

  return {
    menuRef,
    actionMenuItems,
    deleteList,
    duplicateList,
    handleDelete,
    handleDuplicate,
    closeDeleteModal,
    closeDuplicateModal,
    isLoadingDelete,
    isLoadingDuplicate,
  };
};
